import axios, { AxiosInstance } from 'axios'
import { HAPTTIC_X_API_KEY } from 'src/constants/env'
import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import { ANALYTICS_API_URL } from 'src/services/constants/urls'
import {
  DataComparisonCreatePayload,
  DataComparisonListResponse,
  DataComparisonResponse,
  DataComparisonSummaryResponse,
} from './interfaces/dataComparison.interface'

// Social Listening → Data Comparison: one campaign over two periods. The
// campaign-vs-campaign endpoints live on the generated AnalyticsApi client
// under /campaign/compare and are unrelated.
const base = (campaignId: string) =>
  `/v1_private/campaign/${campaignId}/data-comparisons`

// `attachment; filename="…"` -> the filename. Readable cross-origin because the
// endpoint sends Access-Control-Expose-Headers: Content-Disposition.
const readFilename = (response: { headers: Record<string, unknown> }) => {
  const header = response.headers['content-disposition']

  if (typeof header !== 'string') {
    return undefined
  }

  return header.match(/filename="?([^";]+)"?/)?.[1]
}

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Api-Key': HAPTTIC_X_API_KEY,
  'x-session-id': getSessionIdFromLocalStorage() || '',
})

class DataComparisonApiService {
  private static _instance: DataComparisonApiService

  private client: AxiosInstance

  private constructor() {
    this.client = axios.create({
      baseURL: ANALYTICS_API_URL,
      headers: getHeaders(),
    })
  }

  private static get instance(): DataComparisonApiService {
    if (!DataComparisonApiService._instance) {
      DataComparisonApiService._instance = new DataComparisonApiService()
    }

    return DataComparisonApiService._instance
  }

  // Headers are passed per call as well as on the instance: the session id is
  // read at call time, so a login after the singleton was built still sends
  // the current one. Mirrors CustomReportsApiService.
  public static async getList(
    campaignId: string
  ): Promise<DataComparisonListResponse> {
    const { data } =
      await DataComparisonApiService.instance.client.get<DataComparisonListResponse>(
        base(campaignId),
        { headers: getHeaders() }
      )

    return data
  }

  public static async getOne(
    campaignId: string,
    comparisonId: string
  ): Promise<DataComparisonResponse> {
    const { data } =
      await DataComparisonApiService.instance.client.get<DataComparisonResponse>(
        `${base(campaignId)}/${comparisonId}`,
        { headers: getHeaders() }
      )

    return data
  }

  // Fetched as a blob and saved through an object URL rather than navigating to
  // the endpoint: it needs the session headers, which a plain window.open or
  // <a href> cannot send. Mirrors CustomReportsApiService.download.
  public static async download(
    campaignId: string,
    comparisonId: string,
    fallbackFilename: string
  ) {
    const response = await DataComparisonApiService.instance.client.get(
      `${base(campaignId)}/${comparisonId}/download`,
      { headers: getHeaders(), responseType: 'blob' }
    )

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    // The server names the file after the campaign and both periods, so that
    // name is preferred over anything reconstructed here; the fallback only
    // covers a response whose header did not survive.
    link.download = readFilename(response) || fallbackFilename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  public static async create(
    campaignId: string,
    payload: DataComparisonCreatePayload
  ): Promise<DataComparisonSummaryResponse> {
    const { data } =
      await DataComparisonApiService.instance.client.post<DataComparisonSummaryResponse>(
        base(campaignId),
        payload,
        { headers: getHeaders() }
      )

    return data
  }

  public static async remove(
    campaignId: string,
    comparisonId: string
  ): Promise<void> {
    await DataComparisonApiService.instance.client.delete(
      `${base(campaignId)}/${comparisonId}`,
      { headers: getHeaders() }
    )
  }
}

export { DataComparisonApiService }
